import { useCallback, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'

import { Select } from 'antd'

import { AuthResponse, store, switchProductForCompanyUser } from 'core'
import getLoginUser, { User } from 'core/application/account/loginUser'
import { Permissions } from 'core/application/account/permissions'
import { ProductIds } from 'core/application/commons/config'

import Icon from 'common/components/general/Icon'

import useCurrentProduct from 'common/hooks/useCurrentProduct'
import useLoginUser from 'common/hooks/useLoginUser'
import useSwitchProduct from 'common/hooks/useSwitchProduct'

import { useCommandRequest, useQueryRequest } from 'common/hooks'
import { ProductConfig } from 'common/providers/ProductProvider'

import InternalToolsLogo from 'assets/images/InternalToolsMini.svg'
import PeopleKeepLogoMobile from 'assets/images/people-keep-benefits-personalized-mobile.png'
import PeopleKeepLogo from 'assets/images/people-keep-benefits-personalized.svg'

import CompanyPersonalComponent, { CompanyPersonalProps } from './CompanyPersonalComponent'
import HeaderUserSection from './HeaderUserSection'

import './HeaderStyles.less'

const { Option } = Select

export interface UserInfo {
  firstName: string
  lastName: string
  isAdmin: boolean
}

interface HeaderProps extends CompanyPersonalProps {
  userInfo?: UserInfo
  user: User
  translate: (key: string) => string
  setOpenSider?: (openSider: boolean) => void
  openSider?: boolean
  windowWidth?: number
}

/**
 * lg breakpoint = 992,
 * but GrcHRA desktop logo is much wider and displaces other components
 */
const headerBreakpoint = 1050

const DashboardHeader = (props: HeaderProps) => {
  const [user] = useLoginUser()
  const currentProduct = useCurrentProduct()
  const history = useHistory()

  const [isMobile, setIsMobile] = useState(false)

  const isMobileCallback = useCallback(() => {
    window.addEventListener('resize', () => {
      setIsMobile(window.innerWidth < headerBreakpoint)
    })
  }, [setIsMobile])

  useEffect(() => {
    setIsMobile(window.innerWidth < headerBreakpoint)

    isMobileCallback()
    return () => isMobileCallback()
  }, [])

  let linkValue = ''
  switch (currentProduct.productId) {
    case ProductIds.Perks:
      linkValue = 'https://workperks.zendesk.com/hc/en-us'
      break
    case ProductIds.IchraId:
      linkValue = 'https://peoplekeephelpichra2.zendesk.com/hc/en-us'
      break
    case ProductIds.QsehraId:
      linkValue = 'https://peoplekeephelp.zendesk.com/hc/en-us'
      break
    default:
      linkValue = 'https://grouphra.zendesk.com/hc/en-us'
      break
  }

  let productLogo = isMobile
    ? currentProduct.productLogoMobile
      ? currentProduct.productLogoMobile
      : currentProduct.productLogo
    : currentProduct.productLogo

  const handleSwitch = () => {
    window.location.href = '/'
  }

  const { handleSwitchProduct, loading } = useSwitchProduct(handleSwitch)

  const isPeoplekeepInternalUser = (permissions: Permissions[]) => {
    return (
      permissions.includes(Permissions.AccessCsiDashboard) ||
      permissions.includes(Permissions.AccessCsiDashboard) ||
      permissions.includes(Permissions.AccessIchraDecisionTools)
    )
  }

  return (
    <header className="pk--header">
      <section className="pk--header-logo">
        <div className="header-navigation-button" id="header-navigation-button-container"></div>

        <a href="#">
          <img src={PeopleKeepLogo} alt="PeopleKeep Logo" className="pk--header-logo--desktop" />
          <img src={PeopleKeepLogoMobile} alt="PeopleKeep Logo" className="pk--header-logo--mobile" />
        </a>
        {user.products.length > 1 || isPeoplekeepInternalUser(user.permissions) ? (
          <div>
            <Select
              defaultValue={user.productId}
              className="ml-16"
              onChange={(e) => {
                if (e === -1) history.push('/choose-app')
                else handleSwitchProduct(user.companyId, e)
              }}
              loading={loading}
            >
              {user.products.map((product) => (
                <Option value={product.id}>
                  <img
                    className={`pk--header-product-logo t-switch-product-${product.id}`}
                    src={ProductConfig.get(product.id).productLogo}
                    alt=""
                  />
                </Option>
              ))}
              {isPeoplekeepInternalUser(user.permissions) && (
                <Option value={-1}>
                  <img className={`pk--header-product-logo t-switch-product--1`} src={InternalToolsLogo} alt="" />
                </Option>
              )}
            </Select>
          </div>
        ) : (
          <img className="pk--header-product-logo ml-16" src={productLogo} alt="" />
        )}
      </section>

      <div className="flex">
        <section className="tab-header mr-24">
          <a href={linkValue} target="_blank" className="help-center-link" rel="noreferrer">
            <Icon type="far fa-question-circle" fontAwesome /> <span>{props.translate('user-header.help-center')}</span>
          </a>
          {props.userInfo?.isAdmin ? (
            <div className="pk--header-role-toggler">
              <CompanyPersonalComponent {...props} />
            </div>
          ) : null}
        </section>
        <section className="pk--header-right-section">
          <HeaderUserSection user={props.user} userInfo={props.userInfo ?? null} />
        </section>
      </div>
    </header>
  )
}

export default DashboardHeader
